import { useEffect } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { deleteCookie, getCookie, setCookie } from "../../utils/cookies";
import { adminUserType } from "../../utils/postgraphile";
import { samlLogout } from "../../utils/service";
import { userPermission } from "../../routes/oauthRoutes";
import { aemInvalidateCache } from "../../packages/services/cache";
import { setUser } from "../../reducers/users";
import { GET_CURRENT_USER } from "../../utils/graphql";
import { logoutUser } from "../../reducers/signout";
import { Loader } from "../../packages/shared";

const Nav = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isCustomLogin = process.env.REACT_APP_CUSTOM_LOGIN === "true";

    const [getCurrentUser, { loading }] = useLazyQuery(gql`
        ${GET_CURRENT_USER}
    `);

    const token = useSelector((state) => state.tokens.token);
    const currentUser = useSelector((state) => state.users.currentUser);
    const errMessageKey = getCookie("errMessage") || null;
    const relogin = getCookie("relogin") || null;

    let isOauth = null;
    if (currentUser) {
        isOauth = userPermission(currentUser);
    }

    const handleLogout = async () => {
        try {
            await Promise.all([
                deleteCookie("piap_jwt_token"),
                deleteCookie("piap_host_url"),
                samlLogout(),
                !isCustomLogin && setCookie("relogin", true),
            ]);
        } catch (error) {
            toast.error("Logout failed:");
        } finally {
            dispatch(logoutUser());
        }
    };

    const adminNameView = (userType = "") => {
        const { [userType.toString()]: userTypeText = "" } = adminUserType;
        return userTypeText;
    };

    const checkTokenAndGetCurrentUser = async () => {
        try {
            const { data } = await getCurrentUser();
            const { currentUser } = data;
            const isAdmin = userPermission(currentUser).admin;

            if (isAdmin) {
                dispatch(setUser(currentUser));
            } else {
                await handleLogout();
            }
        } catch (error) {
            toast.error(error.message);
            navigate("/sign-in");
            deleteCookie("piap_jwt_token");
            deleteCookie("piap_host_url");
        }
    };

    useEffect(() => {
        if (isCustomLogin) {
            if (!token) {
                navigate("/sign-in");
            } else {
                checkTokenAndGetCurrentUser();
            }
        } else {
            if (errMessageKey || relogin) {
                navigate("/sign-in");
            } else {
                if (!token) {
                    window.location.href = `${process.env.REACT_APP_BASE_URL}/saml/login`;
                } else {
                    checkTokenAndGetCurrentUser();
                }
            }
        }
    }, [token]);

    return (
        (token || errMessageKey || relogin) && (
            <>
                {loading && <Loader />}
                <header id="navbar" className="fixed-top shadow-sm p-3 bg-primary">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-7">
                                <Link
                                    to={currentUser ? "/" : "/sign-in"}
                                    className="h3 text-white"
                                    title="Home"
                                >
                                    Personalised, Identity & Authentication Platform
                                </Link>
                            </div>

                            {currentUser && (
                                <div className="col-md-5 ml-md-auto text-right">
                                    <div className="sign_options row">
                                        {isOauth.siteAdmin && (
                                            <div className="dropdown d-inline-block mr-3">
                                                <button
                                                    className="btn btn-sm btn-light dropdown-toggle"
                                                    type="button"
                                                    id="dropdownMenuButton"
                                                    data-toggle="dropdown"
                                                    aria-haspopup="true"
                                                    aria-expanded="false"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        height="1em"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        <path d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z" />
                                                    </svg>
                                                </button>

                                                <div
                                                    className="dropdown-menu"
                                                    aria-labelledby="dropdownMenuButton"
                                                >
                                                    <Link
                                                        className="dropdown-item"
                                                        to="/activity-log"
                                                    >
                                                        Activity Log
                                                    </Link>
                                                    <button
                                                        onClick={aemInvalidateCache}
                                                        className="dropdown-item"
                                                    >
                                                        Invalidate Cache
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="sign_options">
                                            <Link to={`/profile`} title="Profile">
                                                <button className="btn btn-sm user_btn">
                                                    <span className="d-inline-flex align-items-center">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            height="1em"
                                                            viewBox="0 0 448 512"
                                                        >
                                                            <path
                                                                d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                        <span className="ml-1 mr-2">
                                                            {currentUser.firstName}
                                                        </span>
                                                    </span>

                                                    <span className="badge badge-primary">
                                                        {adminNameView(currentUser.userType)}
                                                    </span>
                                                </button>
                                            </Link>

                                            <button
                                                className="btn btn-sm signout_btn ml-3"
                                                onClick={handleLogout}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    height="1em"
                                                    viewBox="0 0 512 512"
                                                    className="mr-1"
                                                >
                                                    <path
                                                        d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 192 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l210.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128zM160 96c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 32C43 32 0 75 0 128L0 384c0 53 43 96 96 96l64 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-64 0c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l64 0z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                                Sign Out
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
            </>
        )
    );
};

export default Nav;
